(() => {
  'use strict';

  /*
   * Fidello desktop hoofdmenu: rij met "Alles voor je ..."-knoppen boven elk megamenu.
   * Groep 1: de eerste 4 hoofditems (hond, kat, knaagdier, vogel).
   * Groep 2: de volgende 4 hoofditems (paard, kippen & boerderij, erf & weide, ongedierte).
   * Per groep krijgt elke dropdown dezelfde 4 knoppen; de knop van het geopende item is actief.
   */
  const desktopQuery = window.matchMedia('(min-width: 1001px)');
  let retryObserver = null;
  let retryTimeout = null;

  const directChild = (element, selector) =>
    Array.from(element.children).find(child => child.matches(selector));

  const isToggle = a =>
    a.classList.contains('toggle') ||
    a.classList.contains('toggle-back') ||
    a.classList.contains('toggle-wide');

  const directMainLink = item =>
    Array.from(item.children).find(child => child.tagName === 'A' && !isToggle(child));

  // Groep 1: overzichtspagina (/pages/...) zoals voorheen.
  const pageOverview = dropdown => {
    const firstContentColumn = Array.from(dropdown.children).find(child =>
      child.tagName === 'LI' &&
      !child.classList.contains('fidello-species-tabs') &&
      !child.classList.contains('fidello-gratis-promo')
    );
    const firstColumnPageLink = firstContentColumn?.querySelector(
      ':scope > ul > li:first-child > a[href*="/pages/"]'
    );
    if (firstColumnPageLink) return firstColumnPageLink;

    return Array.from(dropdown.querySelectorAll('a[href*="/pages/"]')).find(link =>
      !link.classList.contains('fidello-species-tabs__link')
    );
  };

  // Groep 2: het eerste echte item in de dropdown ("Alles voor je paard", "Alles voor erf & weide", ...).
  const firstRealLink = dropdown =>
    Array.from(dropdown.querySelectorAll('a')).find(link => {
      const href = link.getAttribute('href');
      return !link.classList.contains('fidello-species-tabs__link') &&
        !link.classList.contains('fidello-gratis-promo__link') &&
        !isToggle(link) &&
        href && href !== '#' && !href.startsWith('javascript');
    });

  const menuList = () => {
    const lists = Array.from(document.querySelectorAll(
      '#nav > ul:not(.inner):not(.nav-user):not(.nav-top), #nav-bar > ul'
    ));
    return lists.find(list => window.getComputedStyle(list).display !== 'none');
  };

  const restoreMobile = () => {
    document.documentElement.classList.remove('fidello-menu-ready');
    document.querySelectorAll('.fidello-species-tabs').forEach(row => row.remove());
    document.querySelectorAll('.fidello-species-enhanced').forEach(dropdown => {
      dropdown.classList.remove('fidello-species-enhanced');
      dropdown.style.removeProperty('--fidello-species-columns');
      Array.from(dropdown.classList)
        .filter(name => name.startsWith('fidello-species-cols-'))
        .forEach(name => dropdown.classList.remove(name));
    });
    document.querySelectorAll('.fidello-species-overview-duplicate').forEach(item =>
      item.classList.remove('fidello-species-overview-duplicate')
    );
    document.querySelectorAll('[data-fidello-full-title]').forEach(link => {
      link.removeAttribute('data-fidello-full-title');
      link.removeAttribute('aria-label');
      link.removeAttribute('title');
    });
  };

  const groupData = (items, useMainTitle) => items.map(item => {
    const mainLink = directMainLink(item);
    const dropdown = directChild(item, 'ul');
    const fullLabel = mainLink.querySelector('.fidello-top-label__full');
    const mainTitle = mainLink.dataset.fidelloFullTitle || fullLabel?.textContent.trim() || mainLink.textContent.trim();
    mainLink.dataset.fidelloFullTitle = mainTitle;
    mainLink.setAttribute('aria-label', mainTitle);
    mainLink.setAttribute('title', mainTitle);

    let pageLink = useMainTitle ? pageOverview(dropdown) : firstRealLink(dropdown);
    if (pageLink) {
      const li = pageLink.closest('li');
      // Alleen een overzichtsitem zonder eigen subitems verbergen (dat staat al als knop bovenin).
      if (li && !li.querySelector('ul li')) li.classList.add('fidello-species-overview-duplicate');
    }

    return {
      dropdown,
      fullTitle: useMainTitle ? mainTitle : (pageLink?.textContent.trim() || mainTitle),
      url: pageLink?.getAttribute('href') || mainLink.getAttribute('href') || '/'
    };
  });

  const addTabs = data => {
    data.forEach((current, activeIndex) => {
      if (!current.dropdown || current.dropdown.classList.contains('fidello-species-enhanced')) return;

      const row = document.createElement('li');
      row.className = 'fidello-species-tabs';
      const nav = document.createElement('nav');
      nav.className = 'fidello-species-tabs__nav';
      nav.setAttribute('aria-label', current.fullTitle);

      data.forEach((entry, index) => {
        const link = document.createElement('a');
        link.className = 'fidello-species-tabs__link';
        link.href = entry.url;
        link.textContent = entry.fullTitle;
        if (index === activeIndex) link.setAttribute('aria-current', 'true');
        nav.append(link);
      });

      row.append(nav);
      current.dropdown.prepend(row);
      grid(current.dropdown);
    });
  };

  // Rasterindeling: elke kolom een eigen cel, max. 6 per rij (bij meer: verdeeld over 2 rijen).
  const grid = dropdown => {
    const columns = Array.from(dropdown.children).filter(child =>
      child.tagName === 'LI' &&
      !child.classList.contains('fidello-species-tabs') &&
      !child.classList.contains('fidello-species-overview-duplicate') &&
      // "Gratis producten voor je …"-balk (Fidello Cadeaus): geen kolom, staat over de volle breedte eronder.
      !child.classList.contains('fidello-gratis-promo')
    );
    const visible = columns.length;
    const perRow = visible <= 6 ? Math.max(visible, 1) : Math.ceil(visible / 2);
    dropdown.style.setProperty('--fidello-species-columns', perRow);
    dropdown.classList.add('fidello-species-enhanced', `fidello-species-cols-${perRow}`);
  };

  const enhance = (force) => {
    if (!desktopQuery.matches) {
      restoreMobile();
      return true;
    }

    // Al klaar sinds de vorige trigger: geen volledige her-scan nodig. Bij een echte sectie-herlaad
    // in de theme-editor (force) altijd opnieuw scannen, want de DOM kan dan echt vervangen zijn.
    if (!force && document.documentElement.classList.contains('fidello-menu-ready')) {
      return true;
    }

    const list = menuList();
    if (!list) return false;

    const candidates = Array.from(list.children).filter(item =>
      item.tagName === 'LI' &&
      directMainLink(item) &&
      directChild(item, 'ul')
    );
    if (candidates.length < 4) return false;

    document.documentElement.classList.add('fidello-menu-ready');
    addTabs(groupData(candidates.slice(0, 4), true));
    if (candidates.length >= 8) addTabs(groupData(candidates.slice(4, 8), false));
    // Overige hoofditems (Cadeaus, Aanbiedingen): zelfde raster, zonder knoppenrij.
    candidates.slice(8).forEach(item => {
      const dropdown = directChild(item, 'ul');
      if (dropdown && !dropdown.classList.contains('fidello-species-enhanced')) grid(dropdown);
    });

    return true;
  };

  const stopRetry = () => {
    if (retryObserver) {
      retryObserver.disconnect();
      retryObserver = null;
    }
    if (retryTimeout) {
      window.clearTimeout(retryTimeout);
      retryTimeout = null;
    }
  };

  const start = (force) => {
    if (enhance(force)) {
      stopRetry();
      return;
    }
    if (retryObserver) return;

    const target = document.querySelector('#header-outer') || document.body;
    let debounce = null;
    retryObserver = new MutationObserver(() => {
      if (debounce) return;
      debounce = window.setTimeout(() => {
        debounce = null;
        if (enhance()) stopRetry();
      }, 100);
    });
    retryObserver.observe(target, { childList: true, subtree: true });

    retryTimeout = window.setTimeout(stopRetry, 5000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }

  window.addEventListener('load', () => start(), { once: true });
  window.addEventListener('pageshow', () => start());
  window.addEventListener('popstate', () => start());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) start();
  });

  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', event => {
      if (event.matches) start();
      else restoreMobile();
    });
  }

  document.addEventListener('shopify:section:load', () => start(true));
})();
